//@flow
import * as React from 'react';
import compose from 'recompose/compose';
import { themr, type ThemeType } from 'react-css-themr';
import { connect } from 'react-redux';
import classnames from 'classnames';
import ToastActions from 'common/redux/actions/toast';
import { withRouter } from 'common/components/core/router';
import scrollIntoView from 'scroll-into-view-if-needed';
import isEmpty from 'lodash/isEmpty';
import { translate, type TFunction } from 'react-i18next';
import { Flex, Box, Text, Stack } from '@locus-taxy/neo-ui';
import { ApiError } from 'common/modules/api';
import withRouteTracker from 'common/middleware/RouteTracker';
import { Helmet } from 'react-helmet';
import type { Match } from 'react-router-dom';
import type { List } from 'immutable';

import { teamsSelector } from 'common/redux/selectors/teamSelector';

import Button, { ButtonTypes } from 'common/components/core/button';
import Icon from 'common/components/core/icon';
import { Loader } from 'common/components/core/loader/Loader';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import { COLUMNS } from './PjpMasterContainer';
import PjpExportHandler from './PjpExportHandler';
import PjpBulkViewEmptyState from 'common/entity/pjpMaster/create/PjpBulkViewEmptyState';
import type { TeamModelType } from 'common/models/flow/Team';
import Products from 'common/enums/Product';
import { withUnitConverter } from 'common/containers/intl';
import UnitConversionHelper from 'common/helpers/UnitConversionHelper';

import {
    personnelSelector,
    clientIdSelector
} from 'common/redux/selectors/sessionSelector';
import { getSelectedBUId } from 'common/redux/selectors/appPreferences';
import type { PermanentJourneyPlanModelType } from 'common/models/flow/PermanentJourneyPlan';

import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import PjpInsightsHeader from './PjpInsightsHeader';
import PjpInsightsTable from './PjpInsightsTable';

import theme from './PjpInsightsContainer.scss';
import { dashboardColors } from 'common/enums/ColorEnum';
import Units from 'common/enums/UnitEnum';
import NumberUtils from 'common/utils/NumberUtils';

import { getSelectedBusinessUnit } from 'common/redux/selectors/intl';

import PjpChartsContainer from './PjpChartsContainer';

export const MetricGoodnessEnum = Object.freeze({
    Good: 'GOOD',
    Bad: 'BAD',
    Ok: 'OK'
});

const PjpInsightsTabsEnum = Object.freeze({
    Chart: 'CHART',
    Table: 'TABLE'
});

const DistanceMetricToConvert = ['averageDistancePerBeat'];
const TimeMetricToConvert = ['averageTimePerBeat', 'averageTransactionTime'];

type Props = {
    defaultEmail: string,
    teams: List<TeamModelType>,
    theme: ThemeType,
    t: TFunction,
    match: Match,
    businessUnitId: string,
    unitConverter: UnitConversionHelper,
    showToastAction: Function,
    updateToastAction: Function,
    hideToastAction: Function
};

type State = {
    showExportLoader: boolean,
    exportErrorMessage: ?string,
    isExportActive: boolean,
    planId: ?string,
    pjPlanInsights: ?Object,
    tables: ?Array<Object>,
    tableVisibilityMap: { [key: string]: boolean },
    pjPlan: PermanentJourneyPlanModelType,
    selectedTab: string
};

type InsightHeaderProps = {
    toggleTable: (headerTextId: string) => void,
    headerTextId: string,
    headerText: ?string,
    theme: ThemeType,
    isTableVisible: boolean,
    t: TFunction
};

const preferredDistanceUnit = Units.list.length.Kilometer;
const sourceDistanceUnit = Units.list.length.Meter;
const preferredTimeUnit = Units.list.time.Minute;
const sourceTimeUnit = Units.list.time.Second;

const PjpInsightsSection = ({ t, ...rest }) => {
    return (
        <>
            <Helmet defer={false}>
                <title>{t('pjpInsights')}</title>
            </Helmet>
            <ConnectedPjpInsightsContainer {...rest} />
        </>
    );
};

const TrackedPjpInsights: React.AbstractComponent = withRouteTracker(
    translate()(PjpInsightsSection),
    Products.FIELD_PRO
);
class InsightsTableHeader extends React.PureComponent<InsightHeaderProps> {
    onClick = () => {
        const { toggleTable, headerTextId } = this.props;
        toggleTable(headerTextId);
    };
    render() {
        const {
            theme,
            headerText,
            isTableVisible,
            headerTextId,
            t
        } = this.props;
        return (
            <div
                className={theme.headerCell}
                title={headerText}
                onClick={this.onClick}
            >
                <Icon
                    icon={isTableVisible ? 'caret_down' : 'caret_right'}
                    theme={theme}
                />
                <div className={theme.headerText}>
                    {t(`pjpInsights.${headerTextId}`)}
                </div>
            </div>
        );
    }
}
class PjpInsightsContainer extends React.Component<Props, State> {
    pjpInsightsTableRef: React.ElementRef<any>;
    pjpInsightsChartRef: React.ElementRef<any>;
    constructor(props) {
        super(props);
        this.state = {
            showExportLoader: false,
            exportErrorMessage: null,
            isExportActive: false,
            planId: props.pjpId,
            pjPlanInsights: props.plan.pjPlanInsights,
            tables: this.createInsightsTables(props.plan.pjPlanInsights),
            tableVisibilityMap: this.getTableVisbility(
                props.plan.pjPlanInsights
            ),
            pjPlan: props.plan,
            selectedTab: PjpInsightsTabsEnum.Table
        };
        this.pjpInsightsTableRef = React.createRef(null);
        this.pjpInsightsChartRef = React.createRef(null);
    }

    getTableVisbility = pjPlanInsights => {
        const tableVisibilityObject = {};
        Object.keys(pjPlanInsights).forEach(key => {
            tableVisibilityObject[key] = true;
        });
        return tableVisibilityObject;
    };
    getHeaderForTable = headerProps => {
        const { tableVisibilityMap } = this.state;
        const { theme, t } = this.props;
        const {
            column: { headerText, headerTextId }
        } = headerProps;
        return headerTextId ? (
            <InsightsTableHeader
                t={t}
                theme={theme}
                headerTextId={headerTextId}
                headerText={headerText}
                isTableVisible={tableVisibilityMap[headerTextId]}
                toggleTable={this.toggleTable}
            />
        ) : null;
    };
    toggleTable = table => {
        const isTableVisible = this.state.tableVisibilityMap[table];
        const updatedVisibility = {
            ...this.state.tableVisibilityMap,
            [table]: !isTableVisible
        };
        this.setState({
            tableVisibilityMap: updatedVisibility
        });
    };
    getTableHeaderProps = () => {
        const { t } = this.props;
        return { t };
    };
    getImprovementClass = improvement => {
        const { theme } = this.props;
        if (improvement.metricGoodness === MetricGoodnessEnum.Ok) {
            return;
        }
        if (
            (improvement.metricGoodness === MetricGoodnessEnum.Good &&
                improvement.value) > 0 ||
            (improvement.metricGoodness === MetricGoodnessEnum.Bad &&
                improvement.value < 0)
        ) {
            return theme.increment;
        }
        return theme.decrement;
    };
    getImprovementsIcon = metricValue => {
        if (metricValue === 0) {
            return null;
        } else {
            return metricValue > 0 ? 'increment' : 'decrement';
        }
    };
    getTableColumns = (tableMetrics, tableKey) => {
        const { t } = this.props;
        const { existingPlan, improvementsMap } = tableMetrics;
        const existingPlanColumn = existingPlan
            ? [
                  {
                      Header: t('pjpInsights.existingPlan'),
                      id: 'existingPlan',
                      accessor: 'existingPlan',
                      sortable: false,
                      filterable: false,
                      Cell: props => {
                          const { original: metric } = props;
                          return (
                              <Text>
                                  {metric.existingPlan === null
                                      ? '-'
                                      : metric.existingPlan}
                              </Text>
                          );
                      }
                  }
              ]
            : [];
        const improvementsColumn = !isEmpty(improvementsMap)
            ? [
                  {
                      Header: t('pjpInsights.improvementPercentage'),
                      id: 'improvement',
                      accessor: 'improvementsMap.value',
                      sortable: false,
                      filterable: false,
                      Cell: props => {
                          const { original: metric } = props;
                          if (metric.improvementsMap) {
                              return (
                                  <Flex
                                      className={classnames(
                                          theme.improvements,
                                          this.getImprovementClass(
                                              metric.improvementsMap
                                          )
                                      )}
                                  >
                                      {metric.improvementsMap === null
                                          ? '-'
                                          : Math.abs(
                                                metric.improvementsMap.value
                                            )}{' '}
                                      %
                                      <Icon
                                          icon={this.getImprovementsIcon(
                                              metric.improvementsMap.value
                                          )}
                                          theme={theme}
                                      />
                                  </Flex>
                              );
                          }
                          return <span>-</span>;
                      }
                  }
              ]
            : [];
        const TableColumns = [
            {
                Header: this.getHeaderForTable,
                headerTextId: tableKey,
                id: tableKey,
                accessor: 'key',
                Cell: props => {
                    const { original: metric, column } = props;
                    const { t } = column.getHeaderProps();
                    return (
                        <div className={theme.metricColumn}>
                            {t(`pjpInsights.${metric.key}`)}
                        </div>
                    );
                },
                sortable: false,
                filterable: false,
                getHeaderProps: this.getTableHeaderProps
            },
            ...existingPlanColumn,
            {
                Header: t('pjpInsights.newPlan'),
                id: 'newPlan',
                accessor: 'currentPlan',
                sortable: false,
                filterable: false,
                Cell: props => {
                    const { original: metric } = props;
                    return (
                        <Text>
                            {metric.currentPlan !== null
                                ? metric.currentPlan
                                : '-'}
                        </Text>
                    );
                }
            },
            ...improvementsColumn
        ];
        return TableColumns;
    };
    createTableData = tableMetrics => {
        const { unitConverter } = this.props;
        const { currentPlan, existingPlan, improvementsMap } = tableMetrics;
        const tableData = [];
        Object.keys(currentPlan).forEach(metric => {
            let updatedExistingPlanValue = existingPlan?.[metric];
            let updatedCurrentPlanValue = currentPlan[metric];
            if (DistanceMetricToConvert.includes(metric)) {
                updatedCurrentPlanValue = unitConverter.convertValue({
                    value: NumberUtils.toNumber(
                        updatedCurrentPlanValue
                    ).toFixed(2),
                    sourceUnit: sourceDistanceUnit,
                    prefrredUnits: preferredDistanceUnit
                }).value;
                updatedExistingPlanValue = updatedExistingPlanValue
                    ? unitConverter.convertValue({
                          value: NumberUtils.toNumber(
                              updatedExistingPlanValue
                          ).toFixed(2),
                          sourceUnit: sourceDistanceUnit,
                          prefrredUnits: preferredDistanceUnit
                      }).value
                    : null;
            }
            if (TimeMetricToConvert.includes(metric)) {
                updatedCurrentPlanValue = unitConverter.convertValue({
                    value: NumberUtils.toNumber(
                        updatedCurrentPlanValue
                    ).toFixed(2),
                    sourceUnit: preferredTimeUnit,
                    prefrredUnits: sourceTimeUnit
                }).value;
                updatedExistingPlanValue = updatedExistingPlanValue
                    ? unitConverter.convertValue({
                          value: NumberUtils.toNumber(
                              updatedExistingPlanValue
                          ).toFixed(2),
                          sourceUnit: preferredTimeUnit,
                          prefrredUnits: sourceTimeUnit
                      }).value
                    : null;
            }
            tableData.push({
                key: metric,
                existingPlan: updatedExistingPlanValue,
                currentPlan: updatedCurrentPlanValue,
                improvementsMap:
                    improvementsMap[metric] !== null &&
                    !isEmpty(improvementsMap[metric])
                        ? {
                              value: improvementsMap[metric].value,
                              metricGoodness:
                                  improvementsMap[metric].metricGoodness
                          }
                        : null
            });
        });
        return tableData;
    };
    createInsightsTables = planInsights => {
        const tables = [];
        if (planInsights) {
            Object.keys(planInsights).forEach(key => {
                const metrics = planInsights[key];
                if (metrics) {
                    const tableColumns = this.getTableColumns(metrics, key);
                    const tableData = this.createTableData(metrics);
                    tables.push({
                        table: key,
                        tableColumns,
                        tableData
                    });
                }
            });
            return tables;
        }
        return null;
    };
    scrollTablesIntoView = () => {
        if (this.pjpInsightsTableRef.current) {
            this.setState({
                selectedTab: PjpInsightsTabsEnum.Table
            });
            scrollIntoView(this.pjpInsightsTableRef.current.children[0], {
                block: 'end',
                behavior: 'smooth'
            });
        }
    };
    scrollChartsIntoView = () => {
        if (this.pjpInsightsChartRef.current) {
            this.setState({
                selectedTab: PjpInsightsTabsEnum.Chart
            });
            scrollIntoView(this.pjpInsightsChartRef.current.children[0], {
                block: 'center',
                behavior: 'smooth'
            });
        }
    };
    getFilter = () => {
        return new FilterListBuilder().addAll([]);
    };
    onExportClick = () => {
        this.setState({
            isExportActive: true
        });
    };
    onExportDismiss = () => {
        this.setState({
            isExportActive: false,
            showExportLoader: false
        });
    };
    buildExportRequestParams = () => {
        const { businessUnitId, defaultEmail } = this.props;
        const accessors = new Set();
        let columnAccessors = [];
        COLUMNS.forEach(column => {
            if (typeof column.accessor === 'string') {
                accessors.add(column.accessor);
            } else {
                const value = column.id;
                accessors.add(value);
            }
        });
        columnAccessors = Array.from(accessors);
        // Default Master Filter
        const defaultActiveFilter = new FilterListBuilder()
            .add(
                new FilterBuilder()
                    .withName('status')
                    .withOperation(FilterOperation.Equals)
                    .withValue('ACTIVE')
            )
            .build();
        const requestParams = {
            businessUnitId,
            selectedColumns: columnAccessors,
            emailAddresses: [{ defaultEmail }],
            entityName: 'PJP_EXPORT',
            filters: [...defaultActiveFilter],
            selectedIds: []
        };
        return requestParams;
    };
    getAdditionalExportOptions = () => {
        const { planId } = this.state;
        return { planId };
    };
    handleEntityExportAccept = requestParams => {
        const {
            showToastAction,
            updateToastAction,
            hideToastAction,
            t
        } = this.props;
        const { planId } = this.state;
        const { clientId } = this.state.pjPlan;
        const EXPORT_HANDLER = new PjpExportHandler();
        this.setState({ showExportLoader: true });
        let options = { planId };
        options = {
            entityMasterName: t('pjpInsights.pjpMetrics'),
            ...options
        };
        EXPORT_HANDLER.initialiseAndMakeExportRequest(
            requestParams,
            clientId,
            showToastAction,
            updateToastAction,
            hideToastAction,
            options
        )
            .then(() => {
                this.onExportDismiss();
            })
            .catch(error => {
                this.setState({
                    exportErrorMessage:
                        error instanceof ApiError ? error.message : null,
                    showExportLoader: false
                });
            });
    };
    render() {
        const {
            pjPlan,
            isExportActive,
            tableVisibilityMap,
            tables,
            planId,
            showExportLoader,
            selectedTab
        } = this.state;
        const { businessUnitId, theme, teams, t, history } = this.props;
        if (teams.size && pjPlan) {
            const { name, teamId, clientId } = pjPlan;
            const team = teams.get(teamId.teamId);
            const redirectOptions = {
                buttonLabel: t('pjpInsights.goToFieldPro'),
                url: history.createHref({
                    pathname: `/client/${clientId}/pjp`
                })
            };
            if (tables) {
                return (
                    <Stack className={theme.insightsContainer} height="fluid">
                        <Box className={theme.headerNav}>
                            <PjpInsightsHeader
                                theme={theme}
                                planName={name}
                                team={team}
                                onExportClick={this.onExportClick}
                                t={t}
                            />
                            <Flex className={theme.insightsNav}>
                                <Button
                                    icon="table"
                                    className={classnames({
                                        [theme.insightsNavButton]:
                                            selectedTab !==
                                            PjpInsightsTabsEnum.Table,
                                        [theme.selectedButton]:
                                            selectedTab ===
                                            PjpInsightsTabsEnum.Table
                                    })}
                                    buttonType={ButtonTypes.Outline}
                                    color={
                                        selectedTab ===
                                        PjpInsightsTabsEnum.Table
                                            ? dashboardColors.darkSkyBlue.name
                                            : null
                                    }
                                    label={t('pjpInsights.table')}
                                    onClick={this.scrollTablesIntoView}
                                    semibold
                                />
                                <Button
                                    icon="bar-chart"
                                    className={classnames({
                                        [theme.insightsNavButton]:
                                            selectedTab !==
                                            PjpInsightsTabsEnum.Chart,
                                        [theme.selectedButton]:
                                            selectedTab ===
                                            PjpInsightsTabsEnum.Chart
                                    })}
                                    color={
                                        selectedTab ===
                                        PjpInsightsTabsEnum.Chart
                                            ? dashboardColors.darkSkyBlue.name
                                            : null
                                    }
                                    buttonType={ButtonTypes.Outline}
                                    label={t('pjpInsights.charts')}
                                    onClick={this.scrollChartsIntoView}
                                    semibold
                                />
                            </Flex>
                        </Box>
                        <Box className={theme.tableSection}>
                            <PjpInsightsTable
                                t={t}
                                theme={theme}
                                tableVisibilityMap={tableVisibilityMap}
                                tables={tables}
                                setTableRef={this.pjpInsightsTableRef}
                            />
                            <PjpChartsContainer
                                selectedBU={businessUnitId}
                                setChartRef={this.pjpInsightsChartRef}
                                planId={planId}
                                clientId={clientId}
                                theme={theme}
                                t={t}
                            />
                        </Box>
                        {isExportActive ? (
                            <MasterExportContainer
                                hideSubtitles
                                hideExportOptions
                                defaultEmail={this.props.defaultEmail}
                                showLoader={showExportLoader}
                                onAccept={this.handleEntityExportAccept}
                                appliedFilters={this.getFilter()}
                                onDismiss={this.onExportDismiss}
                                entityNameId="PJP_EXPORT"
                                columns={COLUMNS}
                            />
                        ) : null}
                    </Stack>
                );
            }
            return (
                <PjpBulkViewEmptyState
                    disableSubtitle
                    theme={theme}
                    imageSrc="/img/line-item-master-empty.png"
                    clientId={clientId}
                    redirectOptions={redirectOptions}
                    emptyStateImageSrc="/img/location-master-empty.png"
                    title={t('pjpInsights.metricsNotAvailable')}
                    entityNameId="PJP"
                />
            );
        }
        return (
            <Loader
                backgroundColor={dashboardColors.paleGreySeven}
                theme={theme}
                active
            />
        );
    }
}
const mapStateToProps = (state, ownProps) => {
    const personnel = personnelSelector(state);
    const teams = teamsSelector(state, ownProps);
    const businessUnitId = getSelectedBUId(state, ownProps);
    const clientId = clientIdSelector(state, ownProps);
    const selectedBU = getSelectedBusinessUnit(state, ownProps);
    return {
        ...ownProps,
        defaultEmail: personnel?.email,
        businessUnitId,
        teams,
        clientId,
        selectedBU
    };
};
const withRedux = connect(mapStateToProps, {
    showToastAction: ToastActions.showToast,
    updateToastAction: ToastActions.updateToast,
    hideToastAction: ToastActions.hideToast
});

const ConnectedPjpInsightsContainer = compose(
    themr('PjpInsightsContainer', theme),
    withUnitConverter,
    withRouter,
    withRedux,
    translate('entityManagement')
)(PjpInsightsContainer);
export default TrackedPjpInsights;
